import { Component } from "react";
import { AlertTriangle, RefreshCw, ArrowLeft, Home } from "lucide-react";

/**
 * Global React Error Boundary.
 *
 * Herhangi bir render / lifecycle hatası yakalanır, kullanıcıya beyaz ekran yerine
 * bilgilendirici bir kart gösterir ve "Sayfayı Yenile" / "Ana sayfa" seçenekleri sunar.
 *
 * Prod'da (kasahey.com) hataları console'a düşürür — kullanıcıdan ekran görüntüsü istenebilir.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Konsola tam log — support için kritik
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uygulama hatası:", error);
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Component stack:", info?.componentStack);
    this.setState({ info });
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  reload = () => {
    window.location.reload();
  };

  goHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errMsg = this.state.error?.message || String(this.state.error || "Bilinmeyen hata");
    const isDev = process.env.NODE_ENV !== "production";

    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-lg w-full bg-white rounded-2xl border border-[#EAE6DF] shadow-lg p-8">
          <div className="flex items-start gap-4 mb-4">
            <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-6 w-6 text-rose-600" />
            </div>
            <div>
              <h1 className="text-xl font-display font-semibold text-slate-900">
                Bir sorun oluştu
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Sayfa yüklenirken beklenmedik bir hata meydana geldi. Sayfayı yenilemeyi deneyebilirsiniz.
              </p>
            </div>
          </div>

          <div className="bg-[#FDF6F3] border border-rose-200 rounded-lg p-3 mb-5">
            <div className="text-[11px] uppercase tracking-wider text-rose-700 font-semibold mb-1">
              Hata Detayı
            </div>
            <div className="text-sm text-slate-700 font-mono break-words">{errMsg}</div>
            {isDev && this.state.info?.componentStack && (
              <details className="mt-2 text-[11px] text-slate-500">
                <summary className="cursor-pointer hover:text-slate-700">Component stack (dev)</summary>
                <pre className="mt-1 whitespace-pre-wrap max-h-40 overflow-auto">{this.state.info.componentStack}</pre>
              </details>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={this.reload}
              className="flex-1 h-10 rounded-lg bg-[#D95D39] hover:bg-[#B4451E] text-white font-medium inline-flex items-center justify-center gap-2 transition"
              data-testid="error-reload-btn"
            >
              <RefreshCw className="h-4 w-4" /> Sayfayı Yenile
            </button>
            <button
              onClick={this.goHome}
              className="flex-1 h-10 rounded-lg bg-white border border-[#EAE6DF] hover:bg-[#F9F8F6] text-slate-700 font-medium inline-flex items-center justify-center gap-2 transition"
              data-testid="error-home-btn"
            >
              <Home className="h-4 w-4" /> Ana Sayfa
            </button>
            <button
              onClick={this.reset}
              className="flex-1 h-10 rounded-lg bg-white border border-[#EAE6DF] hover:bg-[#F9F8F6] text-slate-700 font-medium inline-flex items-center justify-center gap-2 transition"
              data-testid="error-back-btn"
            >
              <ArrowLeft className="h-4 w-4" /> Geri Dön
            </button>
          </div>

          <p className="text-[11px] text-slate-400 mt-4 text-center">
            Sorun devam ederse merkez muhasebe ekibiyle iletişime geçin. Hata mesajını ekran görüntüsüyle paylaşabilirsiniz.
          </p>
        </div>
      </div>
    );
  }
}
